export const API_BASE_URL = 'https://patrixglobal.com/backend/api';

class ApiService {
  private getAuthHeaders(): Record<string, string> {
    // Try to get token from localStorage first (backward compatibility)
    // Then check for httpOnly cookie support (more secure)
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest', // CSRF protection
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private getAdminAuthHeaders(): Record<string, string> {
    // Try to get token from localStorage first (backward compatibility)
    // Then check for httpOnly cookie support (more secure)
    const token = localStorage.getItem('admin_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest', // CSRF protection
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private isLoginEndpoint(endpoint: string): boolean {
    // Check if this is a login endpoint where 401 means "invalid credentials" not "expired token"
    return endpoint === '/login' ||
           endpoint === '/register' ||
           endpoint === '/admin/auth/login.php' ||
           endpoint.includes('auth/verify') ||
           endpoint.includes('auth/forgot') ||
           endpoint.includes('auth/reset');
  }

  private handleUnauthorized(endpoint: string): void {
    // Check if this is an admin endpoint
    const isAdminEndpoint = endpoint.startsWith('/admin');

    if (isAdminEndpoint) {
      // Clear admin tokens
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');

      // Redirect to admin login
      window.location.href = '/admin/login';
    } else {
      // Clear user tokens
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      // Redirect to user login
      window.location.href = '/login';
    }
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    // Use appropriate auth headers based on endpoint
    const isAdminEndpoint = endpoint.startsWith('/admin');
    const authHeaders = isAdminEndpoint ? this.getAdminAuthHeaders() : this.getAuthHeaders();

    const config: RequestInit = {
      ...options,
      credentials: 'include', // Include httpOnly cookies
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        // Handle 401 unauthorized responses (expired/invalid tokens)
        // But NOT for login endpoints - those should just return the error message
        if (response.status === 401 && !this.isLoginEndpoint(endpoint)) {
          this.handleUnauthorized(endpoint);
          throw new Error('Session expired. Please login again.');
        }

        throw new Error(data.error || 'Request failed');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  // Authentication
  async register(userData: {
    name: string;
    email: string;
    whatsapp: string;
    password: string;
    language?: string;
  }) {
    const response = await this.request<{
      message: string;
      user?: any;
      token?: string;
      requires_verification?: boolean;
      verification_sent?: boolean;
    }>('/register', {
      method: 'POST',
      body: JSON.stringify({
        ...userData,
        language: userData.language || 'en'
      }),
    });

    // Only set auth if registration is complete (no verification required)
    if (response.token && !response.requires_verification) {
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
    }

    return response;
  }

  async verifyEmail(email: string, otp: string) {
    const response = await this.request<{
      message: string;
      user: any;
      token: string;
    }>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    });

    if (response.token) {
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
    }

    return response;
  }

  async resendVerificationCode(email: string, language: string = 'en') {
    return this.request<{
      message: string;
      verification_sent: boolean;
    }>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email, language }),
    });
  }

  async login(credentials: { email: string; password: string }) {
    const response = await this.request<{
      message: string;
      user: any;
      token: string;
    }>('/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.token) {
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
    }

    return response;
  }

  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }

  // Password Reset
  async forgotPassword(email: string, language: string = 'en') {
    return this.request<{
      message: string;
      reset_code_sent: boolean;
    }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, language }),
    });
  }

  async verifyResetCode(email: string, resetCode: string) {
    return this.request<{
      message: string;
      valid: boolean;
      reset_token?: string;
    }>('/auth/verify-reset-code', {
      method: 'POST',
      body: JSON.stringify({ email, reset_code: resetCode }),
    });
  }

  async resetPassword(email: string, resetToken: string, newPassword: string) {
    return this.request<{
      message: string;
      password_reset: boolean;
    }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({
        email,
        reset_token: resetToken,
        new_password: newPassword
      }),
    });
  }

  // User data
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken');
  }


  // Countries
  async getCountries(language?: string) {
    const params = new URLSearchParams();
    if (language) {
      params.append('language', language);
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      success: boolean;
      countries: {
        id: number;
        name: string;
        name_en: string;
        name_ar: string;
        code: string;
        currency: string;
        flag_emoji: string;
        active: boolean;
        created_at: string;
        updated_at: string;
      }[]
    }>(`/countries${query}`);
  }

  // Packages
  async getPackages(countryId?: number, language?: string) {
    const params = new URLSearchParams();
    if (countryId) {
      params.append('country_id', countryId.toString());
    }
    if (language) {
      params.append('language', language);
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ packages: any[] }>(`/packages/${query}`);
  }

  async getPackage(packageId: string | number) {
    return this.request<{
      success: boolean;
      package: {
        id: number;
        name: string;
        description: string;
        price: string;
        currency: string;
        features: string;
        processing_time: string;
        popular: boolean;
        country_name: string;
        country_code: string;
        country_flag: string;
      }
    }>(`/packages/${packageId}`);
  }

  async getPackageTemplate(packageId: number) {
    return this.request<{
      success: boolean;
      package_id: number;
      package_name: string;
      package_name_ar: string;
      has_template: boolean;
      template?: {
        id: number;
        name: string;
        name_ar: string;
        description: string;
        description_ar: string;
        version: number;
        steps: {
          id: number;
          step_number: number;
          title: string;
          title_ar: string;
          description: string;
          description_ar: string;
          fields: {
            id: number;
            key: string;
            type: string;
            label: string;
            label_ar: string;
            placeholder: string;
            placeholder_ar: string;
            required: boolean;
            validation: any;
            options?: {
              value: string;
              label: string;
              label_ar: string;
            }[];
          }[];
        }[];
      };
    }>(`/packages/template.php?package_id=${packageId}`);
  }

  // User profile and requests
  async getProfile() {
    return this.request<{ user: any }>('/user/profile');
  }

  async getUserRequests() {
    return this.request<{
      requests: {
        id: number;
        request_number: string;
        status: string;
        progress_percentage: number;
        company_name: string; // Extracted from template_data
        business_type: string; // Extracted from template_data
        created_at: string;
        updated_at: string;
        payment_status: string;
        total_amount: number;
        paid_amount: number;
        package_name: string;
        package_price: number;
        package_currency: string;
        country_name: string;
        country_flag: string;
        template_info: {
          id: number | null;
          name: string | null;
          name_ar: string | null;
          is_dynamic: boolean;
        };
        field_preview: {
          key: string;
          value: string;
        }[];
      }[];
    }>('/user/requests.php');
  }

  async getRequestDetails(requestId: number) {
    return this.request<{
      success: boolean;
      request: {
        id: number;
        request_number: string;
        status: string;
        progress_percentage: number;
        company_name: string; // Extracted from template_data
        business_type: string; // Extracted from template_data
        admin_notes?: string;
        payment_status: string;
        total_amount: number;
        paid_amount: number;
        created_at: string;
        updated_at: string;
        submitted_at?: string;
        completed_at?: string;
        package_name: string;
        package_price: number;
        package_currency: string;
        country_name: string;
        country_flag: string;
        template_info: {
          id: number | null;
          name: string | null;
          name_ar: string | null;
          description: string | null;
          description_ar: string | null;
          is_dynamic: boolean;
        };
        dynamic_fields: {
          key: string;
          label: string;
          label_ar: string | null;
          type: string;
          value: any;
          display_value: string;
          is_required: boolean;
          sort_order: number;
        }[];
        documents: {
          id: number;
          document_type: string;
          original_filename: string;
          stored_filename: string;
          file_path: string;
          file_size: number;
          mime_type: string;
          uploaded_by: 'user' | 'admin';
          is_required: boolean;
          is_approved: boolean;
          status: 'pending' | 'approved' | 'rejected';
          admin_comment: string | null;
          uploaded_at: string;
          reviewed_at: string | null;
        }[];
        status_history: {
          id: number;
          old_status: string | null;
          new_status: string;
          changed_by: number | null;
          changed_by_type: 'user' | 'admin' | 'system';
          comment: string | null;
          progress_percentage: number | null;
          created_at: string;
        }[];
      };
    }>(`/user/request_details.php?id=${requestId}`);
  }

  async createRequest(requestData: {
    package_id: number;
    template_id: number;
    template_data: Record<string, any>;
  }) {
    return this.request<{
      success: boolean;
      message: string;
      request: {
        id: number;
        request_number: string;
        status: string;
        progress_percentage: number;
        template: {
          id: number;
          name: string;
        };
        package: {
          id: number;
          name: string;
          price: number;
          currency: string;
        };
      };
    }>('/user/create_request.php', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  }

  // Payments
  async createPaymentSession(requestId: number, locale?: string) {
    return this.request<{
      success: boolean;
      checkout_url: string;
      session_id: string;
      invoice_number: string;
      amount: number;
      currency: string;
      company_name: string;
    }>('/payment/create_checkout_session.php', {
      method: 'POST',
      body: JSON.stringify({
        request_id: requestId,
        locale: locale || 'en'
      }),
    });
  }

  async createPaymentIntent(requestId: number) {
    return this.request<{
      success: boolean;
      client_secret: string;
      payment_intent_id: string;
      amount: number;
      currency: string;
      invoice_number: string;
      payment_id: number;
      request_details: {
        request_number: string;
        company_name: string;
        package_name: string;
        country: string;
      };
    }>('/payment/stripe_integration.php?endpoint=create_payment_intent', {
      method: 'POST',
      body: JSON.stringify({ request_id: requestId }),
    });
  }

  async getDashboardStats() {
    return this.request<{
      success: boolean;
      stats: {
        total_requests: number;
        completed_requests: number;
        in_progress_requests: number;
        rejected_requests: number;
        average_progress: number;
        success_rate: number;
        paid_requests: number;
        pending_payments: number;
        total_amount_due: number;
        total_paid_amount: number;
      };
      recent_requests: {
        id: number;
        request_number: string;
        status: string;
        progress: number;
        company_name: string;
        country: string;
        country_flag: string;
        package_name: string;
        created_at: string;
        status_badge: {
          color: string;
          text: string;
        };
      }[];
      status_distribution: {
        status: string;
        count: number;
      }[];
      monthly_trend: {
        month: string;
        count: number;
      }[];
    }>('/user/dashboard-stats.php');
  }

  // Admin API methods
  async adminLogin(credentials: { username: string; password: string }) {
    const response = await this.request<{
      message: string;
      admin: any;
      token: string;
    }>('/admin/auth/login.php', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.token) {
      localStorage.setItem('admin_token', response.token);
      localStorage.setItem('admin_user', JSON.stringify(response.admin));
    }

    return response;
  }

  async getAdminRequests(filters: any = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request<any>(`/admin/requests/${queryParams ? '?' + queryParams : ''}`);
  }

  async getAdminRequest(requestId: number) {
    return this.request<any>(`/admin/requests/?id=${requestId}`);
  }

  async updateRequestStatus(data: any) {
    return this.request<any>('/admin/requests/?action=update_status', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRequestStatusWithFiles(data: any) {
    return this.request<any>('/admin/requests/?action=update_status_with_files', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async communicateWithUser(data: any) {
    return this.request<any>('/admin/requests/?action=communicate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAdminPackages() {
    return this.request<any>('/admin/packages/');
  }

  async createAdminPackage(data: any) {
    return this.request<any>('/admin/packages/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminPackage(packageId: number, data: any) {
    return this.request<any>(`/admin/packages/?id=${packageId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAdminPackage(packageId: number) {
    return this.request<any>(`/admin/packages/?id=${packageId}`, {
      method: 'DELETE',
      headers: {
        'X-Confirm-Delete': 'true'
      }
    });
  }

  async getAdminCountries() {
    return this.request<any>('/admin/countries/');
  }

  async createAdminCountry(data: any) {
    return this.request<any>('/admin/countries/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminCountry(countryId: number, data: any) {
    return this.request<any>(`/admin/countries/?id=${countryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAdminCountry(countryId: number) {
    return this.request<any>(`/admin/countries/?id=${countryId}`, {
      method: 'DELETE',
      headers: {
        'X-Confirm-Delete': 'true'
      }
    });
  }

  async deleteAdminTemplate(templateId: number) {
    return this.request<any>(`/admin/templates/?id=${templateId}`, {
      method: 'DELETE',
      headers: {
        'X-Confirm-Delete': 'true'
      }
    });
  }

  async getAdminUsers() {
    return this.request<any>('/admin/users/');
  }

  async getAdminAdminUsers() {
    return this.request<any>('/admin/admin-users/');
  }

  async createAdminUser(data: any) {
    return this.request<any>('/admin/users/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createAdminAdminUser(data: any) {
    return this.request<any>('/admin/admin-users/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async toggleUserStatus(data: any) {
    return this.request<any>('/admin/users/toggle-status', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async toggleAdminUserStatus(data: any) {
    return this.request<any>('/admin/admin-users/toggle-status', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAdminTickets() {
    return this.request<any>('/admin/tickets/');
  }

  async getAdminTicket(ticketId: number) {
    return this.request<any>(`/admin/tickets/get.php?id=${ticketId}`);
  }

  async replyToTicket(data: any) {
    return this.request<any>('/admin/tickets/get.php', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTicketStatus(data: any) {
    return this.request<any>('/admin/tickets/update_status.php', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createTicketFromRequest(data: any) {
    return this.request<any>('/admin/tickets/create_from_request.php', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDocumentStatus(data: any) {
    return this.request<any>('/admin/documents/update-status.php', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Admin document download
  async downloadDocument(documentId: number): Promise<Blob> {
    const token = localStorage.getItem('admin_token');
    const url = `${API_BASE_URL}/admin/documents/download.php?id=${documentId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // Handle 401 unauthorized responses for downloads too
      if (response.status === 401) {
        this.handleUnauthorized('/admin/documents/download');
        throw new Error('Session expired. Please login again.');
      }

      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || 'Failed to download document');
    }

    return response.blob();
  }

  // User document download
  async downloadUserDocument(documentId: number): Promise<Blob> {
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}/user/documents/download.php?id=${documentId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // Handle 401 unauthorized responses for downloads too
      if (response.status === 401) {
        this.handleUnauthorized('/user/documents/download');
        throw new Error('Session expired. Please login again.');
      }

      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || 'Failed to download document');
    }

    return response.blob();
  }

  // Admin ticket attachment download
  async downloadTicketAttachment(replyId: number): Promise<Blob> {
    const token = localStorage.getItem('admin_token');
    const url = `${API_BASE_URL}/admin/tickets/download.php?reply_id=${replyId}`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.handleUnauthorized('/admin/tickets/download');
        throw new Error('Session expired. Please login again.');
      }

      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || 'Failed to download attachment');
    }

    return response.blob();
  }

  // User ticket attachment download
  async downloadUserTicketAttachment(replyId: number): Promise<Blob> {
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}/tickets/download.php?reply_id=${replyId}`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.handleUnauthorized('/tickets/download');
        throw new Error('Session expired. Please login again.');
      }

      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || 'Failed to download attachment');
    }

    return response.blob();
  }

  // HTTP method shortcuts
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Service Pages Public API Methods
  async getServicePageBySlug(slug: string, language: 'en' | 'ar' = 'en') {
    try {
      const params = new URLSearchParams({
        slug,
        lang: language
      });

      const response = await fetch(`${API_BASE_URL}/service-pages/public.php?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch service page');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async getServicePageByCountryId(countryId: number, language: 'en' | 'ar' = 'en') {
    try {
      const params = new URLSearchParams({
        country_id: countryId.toString(),
        lang: language
      });

      const response = await fetch(`${API_BASE_URL}/service-pages/public.php?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch service page');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async getServicePagesList(language: 'en' | 'ar' = 'en') {
    try {
      const params = new URLSearchParams({
        action: 'list',
        lang: language
      });

      const response = await fetch(`${API_BASE_URL}/service-pages/public.php?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch service pages list');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }
}

export const apiService = new ApiService();
export default apiService;

// Export service page methods as standalone functions for easier importing
export const getServicePageBySlug = (slug: string, language: 'en' | 'ar' = 'en') =>
  apiService.getServicePageBySlug(slug, language);

export const getServicePageByCountryId = (countryId: number, language: 'en' | 'ar' = 'en') =>
  apiService.getServicePageByCountryId(countryId, language);

export const getServicePagesList = (language: 'en' | 'ar' = 'en') =>
  apiService.getServicePagesList(language);